import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useRole, type AppRole } from "@/hooks/useRole";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Shield,
  Users,
  Activity,
  Flag,
  Settings as SettingsIcon,
  Search,
  Trash2,
  Radar,
  HeartPulse,
  Link as LinkIcon,
  ScrollText,
  TrendingUp,
  Webhook,
  BarChart3,
  LifeBuoy,
  Rocket,
} from "lucide-react";
import { AuditLogsTab } from "@/components/admin/AuditLogsTab";
import { TrendsTab } from "@/components/admin/TrendsTab";
import { WebhookEventsPanel } from "@/components/admin/WebhookEventsPanel";
import { AnalyticsTab } from "@/components/admin/AnalyticsTab";
import { SupportTab } from "@/components/admin/SupportTab";
import { LaunchTab } from "@/components/admin/LaunchTab";
import { ReferralReviewQueue } from "@/components/admin/ReferralReviewQueue";
import { ReferralAuditLogPanel } from "@/components/admin/ReferralAuditLogPanel";
import { RuntimeHealthPanel } from "@/components/admin/RuntimeHealthPanel";

import { SecurityNewFindingsBanner } from "@/components/admin/SecurityNewFindingsBanner";



type RoleRow = {
  id: string;
  user_id: string;
  role: AppRole;
  created_at: string;
};

type Report = {
  id: string;
  reporter_id: string;
  content_type: string;
  content_ref: string;
  reason: string;
  details: string | null;
  status: "pending" | "resolved" | "dismissed";
  moderator_id: string | null;
  moderator_notes: string | null;
  created_at: string;
};

type VerifierLog = {
  id: string;
  correlation_id: string;
  email_domain: string | null;
  outcome: "valid" | "invalid" | "soft_fail" | "error";
  reason: string | null;
  latency_ms: number;
  error_message: string | null;
  created_at: string;
};

type LeadLite = {
  id: string;
  email: string;
  source: string | null;
  created_at: string;
  verifier_correlation_id: string | null;
};

type Tab = "overview" | "launch" | "analytics" | "trends" | "users" | "moderation" | "support" | "referrals" | "verifier" | "health" | "webhooks" | "audit" | "settings";


export default function Admin() {
  const { user } = useAuth();
  const { isAdmin } = useRole();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("overview");

  const [roleRows, setRoleRows] = useState<RoleRow[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [stats, setStats] = useState({
    users: 0,
    licenses: 0,
    leads: 0,
    watches: 0,
    reportsPending: 0,
  });
  const [loading, setLoading] = useState(true);

  // Settings (local-only stub — persists in localStorage)
  const [settings, setSettings] = useState(() => {
    try {
      return (
        JSON.parse(localStorage.getItem("td_admin_settings") || "null") ?? {
          signupsOpen: true,
          aiScanInterval: "10m",
          maintenanceMode: false,
        }
      );
    } catch {
      return { signupsOpen: true, aiScanInterval: "10m", maintenanceMode: false };
    }
  });

  useEffect(() => {
    (async () => {
      const [
        { data: roles },
        { data: reps },
        { count: licenses },
        { count: leads },
        { count: watches },
        { count: prefs },
        { count: pending },
      ] = await Promise.all([
        supabase
          .from("user_roles")
          .select("id, user_id, role, created_at")
          .order("created_at", { ascending: false }),
        supabase
          .from("content_reports")
          .select("*")
          .order("created_at", { ascending: false }),
        supabase.from("licenses").select("id", { count: "exact", head: true }),
        supabase.from("leads").select("id", { count: "exact", head: true }),
        supabase.from("watches").select("id", { count: "exact", head: true }),
        supabase
          .from("user_preferences")
          .select("id", { count: "exact", head: true }),
        supabase
          .from("content_reports")
          .select("id", { count: "exact", head: true })
          .eq("status", "pending"),
      ]);
      setRoleRows((roles as RoleRow[]) ?? []);
      setReports((reps as Report[]) ?? []);
      setStats({
        users: prefs ?? 0,
        licenses: licenses ?? 0,
        leads: leads ?? 0,
        watches: watches ?? 0,
        reportsPending: pending ?? 0,
      });
      setLoading(false);
    })();
  }, []);

  // Realtime: new reports + role changes
  useEffect(() => {
    const ch = supabase
      .channel(`admin-feed:${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "content_reports" },
        (p) => {
          if (p.eventType === "INSERT") {
            setReports((prev) => [p.new as Report, ...prev]);
            setStats((s) => ({ ...s, reportsPending: s.reportsPending + 1 }));
          } else if (p.eventType === "UPDATE") {
            setReports((prev) =>
              prev.map((r) => (r.id === (p.new as Report).id ? (p.new as Report) : r))
            );
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "user_roles" },
        (p) => {
          if (p.eventType === "INSERT") {
            setRoleRows((prev) => [p.new as RoleRow, ...prev]);
          } else if (p.eventType === "DELETE") {
            setRoleRows((prev) => prev.filter((r) => r.id !== (p.old as RoleRow).id));
          }
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, []);

  const saveSettings = (next: typeof settings) => {
    setSettings(next);
    localStorage.setItem("td_admin_settings", JSON.stringify(next));
    toast.success("Platform settings updated");
  };

  return (
    <div className="min-h-screen">
      <header className="border-b border-border">
        <div className="container flex flex-wrap items-center justify-between gap-x-3 gap-y-2 py-3 sm:py-4">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Shield className="size-5 text-primary shrink-0" />
            <div className="font-display text-lg sm:text-xl font-bold whitespace-nowrap">
              Trends Discovery <span className="text-primary">/</span>{" "}
              <span className="text-muted-foreground text-xs sm:text-sm font-mono">admin</span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 text-sm text-muted-foreground">
            <span className="font-mono hidden md:inline truncate max-w-[180px]">{user?.email}</span>
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/security-findings")}>
              Security findings
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/security-register")}>
              Security register
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/dashboard")}>
              Exit admin
            </Button>
          </div>
        </div>
      </header>

      <main className="container py-6 sm:py-8 space-y-8">
        <SecurityNewFindingsBanner />
        {/* Tabs */}

        <nav className="-mx-4 sm:mx-0 flex gap-1 sm:gap-2 overflow-x-auto border-b border-border px-4 sm:px-0 -mb-px [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {(
            [
              { id: "overview", label: "Overview", icon: Activity },
              { id: "launch", label: "Launch", icon: Rocket },
              { id: "analytics", label: "Analytics", icon: BarChart3 },
              { id: "trends", label: "Trends", icon: TrendingUp },
              { id: "users", label: "Users & roles", icon: Users },
              { id: "moderation", label: "Moderation", icon: Flag },
              { id: "support", label: "Support", icon: LifeBuoy },
              { id: "referrals", label: "Referrals", icon: Flag },
              { id: "verifier", label: "Verifier logs", icon: Radar },
              { id: "health", label: "Runtime health", icon: HeartPulse },
              { id: "webhooks", label: "Webhooks", icon: Webhook },
              { id: "audit", label: "Audit logs", icon: ScrollText },
              { id: "settings", label: "Platform", icon: SettingsIcon },
            ] as { id: Tab; label: string; icon: typeof Activity }[]
          ).map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2.5 font-mono text-xs uppercase border-b-2 transition-colors whitespace-nowrap shrink-0 ${
                tab === t.id
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <t.icon className="size-3.5" />
              {t.label}
              {t.id === "moderation" && stats.reportsPending > 0 && (
                <span className="ml-1 rounded-full bg-accent text-accent-foreground text-[10px] px-1.5 py-0.5">
                  {stats.reportsPending}
                </span>
              )}
            </button>
          ))}
        </nav>

        {tab === "overview" && (
          <OverviewTab stats={stats} loading={loading} reports={reports} />
        )}
        {tab === "launch" && <LaunchTab />}
        {tab === "analytics" && <AnalyticsTab />}
        {tab === "trends" && <TrendsTab />}
        {tab === "users" && (
          <UsersTab roleRows={roleRows} setRoleRows={setRoleRows} isAdmin={isAdmin} />
        )}
        {tab === "moderation" && (
          <ModerationTab
            reports={reports}
            setReports={setReports}
            setStats={setStats}
            moderatorId={user?.id ?? null}
          />
        )}
        {tab === "support" && <SupportTab />}
        {tab === "referrals" && (
          <div className="space-y-6">
            <ReferralReviewQueue />
            <ReferralAuditLogPanel />
          </div>
        )}

        {tab === "verifier" && <VerifierLogsTab />}
        {tab === "health" && <RuntimeHealthPanel />}
        {tab === "webhooks" && <WebhookEventsPanel />}
        {tab === "audit" && <AuditLogsTab />}
        {tab === "settings" && (
          <SettingsTab settings={settings} onSave={saveSettings} />
        )}
      </main>
    </div>
  );
}

function OverviewTab({
  stats,
  loading,
  reports,
}: {
  stats: {
    users: number;
    licenses: number;
    leads: number;
    watches: number;
    reportsPending: number;
  };
  loading: boolean;
  reports: Report[];
}) {
  const recent = reports.slice(0, 5);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat label="Onboarded users" value={stats.users} />
        <Stat label="Active licenses" value={stats.licenses} />
        <Stat label="Leads captured" value={stats.leads} />
        <Stat label="Watches saved" value={stats.watches} />
        <Stat
          label="Pending reports"
          value={stats.reportsPending}
          tone={stats.reportsPending > 0 ? "alert" : "default"}
        />
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold">Recent moderation activity</h2>
          <span className="font-mono text-xs text-muted-foreground">
            {loading ? "loading…" : `${reports.length} total`}
          </span>
        </div>
        {recent.length === 0 ? (
          <p className="text-sm text-muted-foreground">No reports yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {recent.map((r) => (
              <li key={r.id} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm truncate">{r.reason}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">
                    {r.content_type} · {r.content_ref}
                  </div>
                </div>
                <StatusBadge status={r.status} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function UsersTab({
  roleRows,
  setRoleRows,
  isAdmin,
}: {
  roleRows: RoleRow[];
  setRoleRows: React.Dispatch<React.SetStateAction<RoleRow[]>>;
  isAdmin: boolean;
}) {
  const [grantUserId, setGrantUserId] = useState("");
  const [grantRole, setGrantRole] = useState<AppRole>("moderator");
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(
    () =>
      roleRows.filter(
        (r) =>
          !filter ||
          r.user_id.toLowerCase().includes(filter.toLowerCase()) ||
          r.role.includes(filter.toLowerCase())
      ),
    [roleRows, filter]
  );

  const grant = async () => {
    if (!grantUserId.trim()) return toast.error("Enter a user UUID");
    setBusy(true);
    const { data, error } = await supabase
      .from("user_roles")
      .insert({ user_id: grantUserId.trim(), role: grantRole })
      .select()
      .single();
    setBusy(false);
    if (error) return toast.error(error.message);
    setRoleRows((prev) => [data as RoleRow, ...prev]);
    setGrantUserId("");
    toast.success(`Granted ${grantRole}`);
  };

  const revoke = async (id: string) => {
    const { error } = await supabase.from("user_roles").delete().eq("id", id);
    if (error) return toast.error(error.message);
    setRoleRows((prev) => prev.filter((r) => r.id !== id));
    toast.success("Role revoked");
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-card p-6">
        <h2 className="font-display text-xl font-bold mb-1">Grant role</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Paste a user UUID (from your auth records) and assign admin or moderator
          access.
        </p>
        <div className="grid sm:grid-cols-[1fr_180px_120px] gap-2">
          <Input
            placeholder="user UUID (e.g. 8f3c…)"
            value={grantUserId}
            onChange={(e) => setGrantUserId(e.target.value)}
            className="font-mono"
          />
          <Select value={grantRole} onValueChange={(v) => setGrantRole(v as AppRole)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">admin</SelectItem>
              <SelectItem value="moderator">moderator</SelectItem>
              <SelectItem value="user">user</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={grant} disabled={busy || !isAdmin}>
            Grant
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4 gap-3">
          <h2 className="font-display text-xl font-bold">Role assignments</h2>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="filter"
              className="pl-7 h-8 w-48 font-mono text-xs"
            />
          </div>
        </div>
        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground">No matching role assignments.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase text-muted-foreground border-b border-border">
                  <th className="py-2">User</th>
                  <th>Role</th>
                  <th>Granted</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-border/60">
                    <td className="py-2 font-mono text-xs truncate max-w-[260px]">
                      {r.user_id}
                    </td>
                    <td>
                      <span className="font-mono text-[10px] uppercase rounded px-1.5 py-0.5 border border-primary/40 text-primary">
                        {r.role}
                      </span>
                    </td>
                    <td className="font-mono text-[10px] text-muted-foreground">
                      {new Date(r.created_at).toLocaleDateString()}
                    </td>
                    <td className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => revoke(r.id)}
                        disabled={!isAdmin}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ModerationTab({
  reports,
  setReports,
  setStats,
  moderatorId,
}: {
  reports: Report[];
  setReports: React.Dispatch<React.SetStateAction<Report[]>>;
  setStats: React.Dispatch<
    React.SetStateAction<{
      users: number;
      licenses: number;
      leads: number;
      watches: number;
      reportsPending: number;
    }>
  >;
  moderatorId: string | null;
}) {
  const [filter, setFilter] = useState<"all" | Report["status"]>("pending");
  const [notes, setNotes] = useState<Record<string, string>>({});

  const visible = reports.filter((r) => filter === "all" || r.status === filter);

  const setStatus = async (r: Report, status: Report["status"]) => {
    const patch = {
      status,
      moderator_id: moderatorId,
      moderator_notes: notes[r.id] ?? r.moderator_notes,
    };
    const { data, error } = await supabase
      .from("content_reports")
      .update(patch)
      .eq("id", r.id)
      .select()
      .single();
    if (error) return toast.error(error.message);
    setReports((prev) => prev.map((x) => (x.id === r.id ? (data as Report) : x)));
    if (r.status === "pending" && status !== "pending") {
      setStats((s) => ({ ...s, reportsPending: Math.max(0, s.reportsPending - 1) }));
    }
    toast.success(`Marked ${status}`);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {(["pending", "resolved", "dismissed", "all"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`text-xs font-mono uppercase rounded-full border px-3 py-1 transition-colors ${
              filter === f
                ? "border-primary bg-primary/10 text-primary"
                : "border-border text-muted-foreground hover:border-primary/50"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
          No reports in this view.
        </div>
      ) : (
        <ul className="space-y-3">
          {visible.map((r) => (
            <li
              key={r.id}
              className="rounded-xl border border-border bg-card p-5 space-y-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display text-base font-semibold">{r.reason}</div>
                  <div className="font-mono text-[10px] text-muted-foreground mt-1">
                    {r.content_type} · {r.content_ref} ·{" "}
                    {new Date(r.created_at).toLocaleString()}
                  </div>
                </div>
                <StatusBadge status={r.status} />
              </div>

              {r.details && (
                <p className="text-sm text-muted-foreground border-l-2 border-border pl-3">
                  {r.details}
                </p>
              )}

              <Textarea
                placeholder="Moderator notes (optional)"
                defaultValue={r.moderator_notes ?? ""}
                onChange={(e) =>
                  setNotes((prev) => ({ ...prev, [r.id]: e.target.value }))
                }
                rows={2}
                className="text-sm"
              />

              <div className="flex flex-wrap gap-2 justify-end">
                {r.status !== "resolved" && (
                  <Button size="sm" onClick={() => setStatus(r, "resolved")}>
                    Resolve
                  </Button>
                )}
                {r.status !== "dismissed" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setStatus(r, "dismissed")}
                  >
                    Dismiss
                  </Button>
                )}
                {r.status !== "pending" && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setStatus(r, "pending")}
                  >
                    Reopen
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SettingsTab({
  settings,
  onSave,
}: {
  settings: { signupsOpen: boolean; aiScanInterval: string; maintenanceMode: boolean };
  onSave: (s: typeof settings) => void;
}) {
  const [draft, setDraft] = useState(settings);
  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);

  return (
    <div className="rounded-xl border border-border bg-card p-6 max-w-2xl space-y-5">
      <div>
        <h2 className="font-display text-xl font-bold">Platform settings</h2>
        <p className="text-sm text-muted-foreground">
          Lightweight controls saved locally. Wire these to a settings table when ready.
        </p>
      </div>

      <Toggle
        label="Public signups open"
        hint="Allow new users to register and check out."
        value={draft.signupsOpen}
        onChange={(v) => setDraft({ ...draft, signupsOpen: v })}
      />
      <Toggle
        label="Maintenance mode"
        hint="Display a banner and pause AI scans."
        value={draft.maintenanceMode}
        onChange={(v) => setDraft({ ...draft, maintenanceMode: v })}
      />

      <div>
        <label className="font-mono text-xs text-primary">AI scan interval</label>
        <Select
          value={draft.aiScanInterval}
          onValueChange={(v) => setDraft({ ...draft, aiScanInterval: v })}
        >
          <SelectTrigger className="mt-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="5m">Every 5 minutes</SelectItem>
            <SelectItem value="10m">Every 10 minutes</SelectItem>
            <SelectItem value="30m">Every 30 minutes</SelectItem>
            <SelectItem value="1h">Hourly</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex justify-end">
        <Button onClick={() => onSave(draft)} disabled={!dirty}>
          Save settings
        </Button>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number;
  tone?: "default" | "alert";
}) {
  return (
    <div
      className={`rounded-xl border p-4 ${
        tone === "alert"
          ? "border-accent/40 bg-accent/5"
          : "border-border bg-card"
      }`}
    >
      <div className="font-mono text-[10px] uppercase text-muted-foreground">
        {label}
      </div>
      <div className="font-display text-3xl font-bold mt-2">{value}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: Report["status"] }) {
  const map = {
    pending: "border-accent/40 text-accent",
    resolved: "border-primary/40 text-primary",
    dismissed: "border-border text-muted-foreground",
  } as const;
  return (
    <span
      className={`shrink-0 font-mono text-[10px] uppercase rounded px-1.5 py-0.5 border ${map[status]}`}
    >
      {status}
    </span>
  );
}

function Toggle({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-1">
      <div>
        <div className="text-sm">{label}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        className={`relative h-6 w-11 rounded-full border transition-colors ${
          value ? "bg-primary border-primary" : "bg-muted border-border"
        }`}
      >
        <span
          className={`absolute top-0.5 size-5 rounded-full bg-background transition-all ${
            value ? "left-[22px]" : "left-0.5"
          }`}
        />
      </button>
    </div>
  );
}

type VerifierStats = {
  total: number;
  valid_count: number;
  invalid_count: number;
  soft_fail_count: number;
  error_count: number;
  error_rate: number;
  p50_ms: number;
  p95_ms: number;
  p99_ms: number;
};

type LatencyBucket = {
  bucket: number;
  range_start: number;
  range_end: number;
  count: number;
};

type LatencyBox = {
  outcome: VerifierLog["outcome"];
  count: number;
  min_ms: number;
  q1_ms: number;
  median_ms: number;
  q3_ms: number;
  max_ms: number;
};

function VerifierLogsTab() {
  const [logs, setLogs] = useState<VerifierLog[]>([]);
  const [stats, setStats] = useState<VerifierStats | null>(null);
  const [histogram, setHistogram] = useState<LatencyBucket[]>([]);
  const [boxes, setBoxes] = useState<LatencyBox[]>([]);
  const [chartView, setChartView] = useState<"histogram" | "boxplot">("histogram");
  const [chartError, setChartError] = useState<string | null>(null);
  const [chartRetry, setChartRetry] = useState<{ attempt: number; max: number; delayMs: number; startedAt: number; remainingDelays: number[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [outcome, setOutcome] = useState<"all" | VerifierLog["outcome"]>("all");
  const [minLatency, setMinLatency] = useState<string>("");
  const [maxLatency, setMaxLatency] = useState<string>("");
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [linkedLead, setLinkedLead] = useState<LeadLite | null>(null);
  const [leadLoading, setLeadLoading] = useState(false);
  const cancelRetryRef = useRef<(() => void) | null>(null);
  const cancelToastShownRef = useRef(false);
  const lastCancelAtRef = useRef(0);
  const CANCEL_THROTTLE_MS = 1000;
  const [cancelDisabled, setCancelDisabled] = useState(false);

  const cancelChartRetry = () => {
    const now = Date.now();
    if (now - lastCancelAtRef.current < CANCEL_THROTTLE_MS) return;
    lastCancelAtRef.current = now;
    if (!cancelRetryRef.current || cancelToastShownRef.current) return;
    cancelToastShownRef.current = true;
    setCancelDisabled(true);
    const remaining = chartRetry ? Math.max(0, chartRetry.max - chartRetry.attempt) : 0;
    const attempt = chartRetry?.attempt ?? null;
    const max = chartRetry?.max ?? null;
    cancelRetryRef.current();

    // Fire-and-forget audit log entry
    supabase
      .rpc("log_admin_action", {
        _action: "verifier.cancel_retry_sequence",
        _details: {
          attempt,
          max,
          remaining_skipped: remaining,
          filters: {
            outcome: outcome === "all" ? null : outcome,
            min_latency: minLatency || null,
            max_latency: maxLatency || null,
            from: from || null,
            to: to || null,
            correlation_id: search.trim() || null,
          },
          client_ts: new Date().toISOString(),
        },
      })
      .then(({ error }) => {
        if (error) console.warn("audit log failed", error.message);
      });

    toast.info(
      remaining === 0
        ? "Retry sequence cancelled — no retries remaining. Showing error state."
        : `Retry sequence cancelled — ${remaining} retr${remaining === 1 ? "y" : "ies"} skipped. Showing error state.`,
    );
  };

  const load = async () => {
    cancelToastShownRef.current = false;
    lastCancelAtRef.current = 0;
    setCancelDisabled(false);
    setLoading(true);
    setChartError(null);
    setChartRetry(null);
    const minN = minLatency ? Number(minLatency) : null;
    const maxN = maxLatency ? Number(maxLatency) : null;
    const filters = {
      _outcome: outcome === "all" ? null : outcome,
      _min_latency: minN !== null && !Number.isNaN(minN) ? minN : null,
      _max_latency: maxN !== null && !Number.isNaN(maxN) ? maxN : null,
      _from: from ? new Date(from).toISOString() : null,
      _to: to ? new Date(to).toISOString() : null,
      _correlation_id: search.trim() || null,
    };
    const [logsRes, statsRes] = await Promise.all([
      supabase.rpc("get_verifier_logs", { ...filters, _limit: 500 }),
      supabase.rpc("get_verifier_stats", filters),
    ]);
    if (logsRes.error) { setLoading(false); return toast.error(logsRes.error.message); }
    if (statsRes.error) { setLoading(false); return toast.error(statsRes.error.message); }
    setLogs((logsRes.data as VerifierLog[]) ?? []);
    const row = Array.isArray(statsRes.data) ? statsRes.data[0] : statsRes.data;
    setStats(row ? (row as VerifierStats) : null);

    // Latency RPCs with exponential backoff (3 attempts: 0ms, 400ms, 1200ms), cancellable
    const delays = [0, 400, 1200];
    let cancelled = false;
    let lastErr: string | null = null;
    for (let i = 0; i < delays.length; i++) {
      if (delays[i] > 0) {
        setChartRetry({
          attempt: i + 1,
          max: delays.length,
          delayMs: delays[i],
          startedAt: Date.now(),
          remainingDelays: delays.slice(i + 1).filter((d) => d > 0),
        });
        const sleepCancelled = await new Promise<boolean>((resolve) => {
          const t = setTimeout(() => {
            cancelRetryRef.current = null;
            resolve(false);
          }, delays[i]);
          cancelRetryRef.current = () => {
            clearTimeout(t);
            cancelled = true;
            cancelRetryRef.current = null;
            resolve(true);
          };
        });
        if (sleepCancelled) break;
      }
      const [histRes, boxRes] = await Promise.all([
        supabase.rpc("get_verifier_latency_histogram", { ...filters, _buckets: 24 }),
        supabase.rpc("get_verifier_latency_boxplot", filters),
      ]);
      if (!histRes.error && !boxRes.error) {
        setHistogram(((histRes.data as LatencyBucket[]) ?? []).map(b => ({ ...b, count: Number(b.count) })));
        setBoxes(((boxRes.data as LatencyBox[]) ?? []).map(b => ({ ...b, count: Number(b.count) })));
        setChartError(null);
        setChartRetry(null);
        setLoading(false);
        cancelRetryRef.current = null;
        return;
      }
      lastErr = histRes.error?.message ?? boxRes.error?.message ?? "Failed to load latency data";
    }
    setHistogram([]);
    setBoxes([]);
    setChartError(cancelled ? (lastErr ?? "Retries cancelled before any successful response") : lastErr);
    setChartRetry(null);
    cancelRetryRef.current = null;
    setLoading(false);
  };


  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const findLead = async (correlationId: string) => {
    setLeadLoading(true);
    setLinkedLead(null);
    const { data, error } = await supabase
      .from("leads")
      .select("id, email, source, created_at, verifier_correlation_id")
      .eq("verifier_correlation_id", correlationId)
      .maybeSingle();
    setLeadLoading(false);
    if (error) return toast.error(error.message);
    if (!data) return toast.message("No lead linked to this correlation id");
    setLinkedLead(data as LeadLite);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display text-xl font-bold">Verifier observability</h2>
          <span className="font-mono text-xs text-muted-foreground">
            {loading ? "loading…" : `${logs.length} rows`}
          </span>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-2">
          <div>
            <label className="font-mono text-[10px] uppercase text-muted-foreground">Outcome</label>
            <Select value={outcome} onValueChange={(v) => setOutcome(v as typeof outcome)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="valid">valid</SelectItem>
                <SelectItem value="invalid">invalid</SelectItem>
                <SelectItem value="soft_fail">soft_fail</SelectItem>
                <SelectItem value="error">error</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase text-muted-foreground">Min ms</label>
            <Input
              type="number"
              inputMode="numeric"
              value={minLatency}
              onChange={(e) => setMinLatency(e.target.value)}
              className="mt-1 font-mono"
              placeholder="0"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase text-muted-foreground">Max ms</label>
            <Input
              type="number"
              inputMode="numeric"
              value={maxLatency}
              onChange={(e) => setMaxLatency(e.target.value)}
              className="mt-1 font-mono"
              placeholder="∞"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase text-muted-foreground">From</label>
            <Input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="mt-1 font-mono"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase text-muted-foreground">To</label>
            <Input
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1 font-mono"
            />
          </div>
          <div className="flex items-end">
            <Button onClick={load} disabled={loading} className="w-full">
              Apply
            </Button>
          </div>
        </div>

        <div className="grid sm:grid-cols-[1fr_auto_auto] gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="correlation id (uuid) — exact match"
              className="pl-7 font-mono text-xs"
            />
          </div>
          <Button variant="outline" onClick={load} disabled={loading}>
            Search
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setOutcome("all");
              setMinLatency("");
              setMaxLatency("");
              setFrom("");
              setTo("");
              setSearch("");
              setLinkedLead(null);
              setTimeout(load, 0);
            }}
          >
            Reset
          </Button>
        </div>
      </div>

      {(stats || loading) && (
        <div className="space-y-2">
          {stats ? (
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
              <Stat label="Total (filtered)" value={stats.total} />
              <Stat
                label="Error rate %"
                value={Number(stats.error_rate)}
                tone={Number(stats.error_rate) > 5 ? "alert" : "default"}
              />
              <Stat label="p50 ms" value={stats.p50_ms} />
              <Stat label="p95 ms" value={stats.p95_ms} />
              <Stat label="p99 ms" value={stats.p99_ms} />
              <Stat label="Invalid" value={stats.invalid_count} />
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-[78px] rounded-xl border border-border bg-card animate-pulse" />
              ))}
            </div>
          )}
          <p className="font-mono text-[10px] text-muted-foreground">
            Stats reflect the full filtered dataset. Table shows the most recent 500 matching rows.
          </p>


          <div className="rounded-xl border border-border bg-card p-4 mt-3">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <h3 className="font-display text-sm font-bold">Latency distribution</h3>
              <div className="flex items-center gap-2">
                <div className="inline-flex rounded-md border border-border overflow-hidden">
                  {(["histogram", "boxplot"] as const).map((v) => (
                    <button
                      key={v}
                      onClick={() => setChartView(v)}
                      className={`font-mono text-[10px] uppercase px-2.5 py-1 transition-colors ${
                        chartView === v
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
                <span className="font-mono text-[10px] text-muted-foreground">
                  {loading
                    ? "loading…"
                    : chartView === "histogram"
                    ? `ms · ${histogram.length} buckets`
                    : `ms · ${boxes.length} groups`}
                </span>
              </div>
            </div>

            {chartRetry ? (
              <ChartRetrying attempt={chartRetry.attempt} max={chartRetry.max} delayMs={chartRetry.delayMs} startedAt={chartRetry.startedAt} remainingDelays={chartRetry.remainingDelays} onCancel={cancelChartRetry} cancelDisabled={cancelDisabled} />
            ) : loading ? (
              <ChartSkeleton />
            ) : chartError ? (
              <ChartError message={chartError} onRetry={load} />
            ) : chartView === "histogram" ? (
              histogram.length === 0 ? (
                <ChartEmpty message="No latency samples match these filters." />
              ) : (
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={histogram} margin={{ top: 4, right: 8, bottom: 4, left: -8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis
                        dataKey="range_start"
                        tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10, fontFamily: "monospace" }}
                        tickLine={false}
                        axisLine={{ stroke: "hsl(var(--border))" }}
                        tickFormatter={(v) => `${v}`}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10, fontFamily: "monospace" }}
                        tickLine={false}
                        axisLine={{ stroke: "hsl(var(--border))" }}
                        width={40}
                      />
                      <RTooltip
                        cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
                        contentStyle={{
                          background: "hsl(var(--popover))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: 6,
                          fontSize: 12,
                        }}
                        labelFormatter={(_, items) => {
                          const b = items?.[0]?.payload as LatencyBucket | undefined;
                          return b ? `${b.range_start}–${b.range_end} ms` : "";
                        }}
                        formatter={(v: number) => [v, "requests"]}
                      />
                      <Bar dataKey="count" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )
            ) : boxes.length === 0 ? (
              <ChartEmpty message="No outcome groups to compare." />
            ) : (
              <BoxplotChart boxes={boxes} />
            )}

            {!loading && stats && stats.total > 0 && (
              <div className="mt-2 font-mono text-[10px] text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                <span>p50: {stats.p50_ms}ms</span>
                <span>p95: {stats.p95_ms}ms</span>
                <span>p99: {stats.p99_ms}ms</span>
              </div>
            )}
          </div>

        </div>
      )}

      {linkedLead && (
        <div className="rounded-xl border border-primary/40 bg-primary/5 p-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase text-primary mb-1">
              Linked lead
            </div>
            <div className="text-sm truncate">{linkedLead.email}</div>
            <div className="font-mono text-[10px] text-muted-foreground mt-1">
              {linkedLead.source ?? "—"} · {new Date(linkedLead.created_at).toLocaleString()} · {linkedLead.id}
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={() => setLinkedLead(null)}>
            Close
          </Button>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        {logs.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">
            {loading ? "Loading verifier logs…" : "No verifier logs match these filters."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-mono text-[10px] uppercase text-muted-foreground border-b border-border">
                <th className="py-2 px-4">When</th>
                <th>Outcome</th>
                <th>Latency</th>
                <th>Domain</th>
                <th>Reason / error</th>
                <th>Correlation</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-b border-border/60 align-top">
                  <td className="py-2 px-4 font-mono text-[11px] text-muted-foreground whitespace-nowrap">
                    {new Date(l.created_at).toLocaleString()}
                  </td>
                  <td>
                    <OutcomeBadge outcome={l.outcome} />
                  </td>
                  <td className="font-mono text-xs whitespace-nowrap">{l.latency_ms}ms</td>
                  <td className="font-mono text-xs">{l.email_domain ?? "—"}</td>
                  <td className="text-xs max-w-[280px] truncate">
                    {l.error_message ? (
                      <span className="text-destructive">{l.error_message}</span>
                    ) : (
                      l.reason ?? "—"
                    )}
                  </td>
                  <td className="font-mono text-[10px] text-muted-foreground">
                    <button
                      className="hover:text-primary truncate inline-block max-w-[140px] align-middle"
                      onClick={() => {
                        setSearch(l.correlation_id);
                        navigator.clipboard?.writeText(l.correlation_id).catch(() => {});
                        toast.success("Correlation id copied");
                      }}
                      title={l.correlation_id}
                    >
                      {l.correlation_id}
                    </button>
                  </td>
                  <td className="text-right pr-3">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => findLead(l.correlation_id)}
                      disabled={leadLoading}
                    >
                      <LinkIcon className="size-3.5" />
                      <span className="ml-1 hidden sm:inline">Lead</span>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function OutcomeBadge({ outcome }: { outcome: VerifierLog["outcome"] }) {
  const map = {
    valid: "border-primary/40 text-primary",
    invalid: "border-accent/40 text-accent",
    soft_fail: "border-yellow-500/40 text-yellow-500",
    error: "border-destructive/40 text-destructive",
  } as const;
  return (
    <span
      className={`shrink-0 font-mono text-[10px] uppercase rounded px-1.5 py-0.5 border ${map[outcome]}`}
    >
      {outcome}
    </span>
  );
}

function BoxplotChart({ boxes }: { boxes: LatencyBox[] }) {
  if (boxes.length === 0) {
    return (
      <div className="h-56 flex items-center justify-center text-sm text-muted-foreground">
        No data for boxplot.
      </div>
    );
  }

  // Layout
  const W = 720;
  const H = 224;
  const padL = 56;
  const padR = 16;
  const padT = 12;
  const padB = 28;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const lo = Math.min(...boxes.map((b) => b.min_ms));
  const hi = Math.max(...boxes.map((b) => b.max_ms));
  const span = hi - lo || 1;
  const y = (v: number) => padT + innerH - ((v - lo) / span) * innerH;

  const colWidth = innerW / boxes.length;
  const boxWidth = Math.min(48, colWidth * 0.5);

  const colorByOutcome: Record<string, string> = {
    valid: "hsl(var(--primary))",
    invalid: "hsl(var(--accent))",
    soft_fail: "hsl(48 96% 53%)",
    error: "hsl(var(--destructive))",
  };

  // Y ticks
  const ticks = 4;
  const tickValues = Array.from({ length: ticks + 1 }, (_, i) =>
    Math.round(lo + (span * i) / ticks)
  );

  return (
    <div className="w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-56"
        role="img"
        aria-label="Latency boxplot by outcome"
      >
        {/* Grid + Y axis */}
        {tickValues.map((t) => (
          <g key={t}>
            <line
              x1={padL}
              x2={W - padR}
              y1={y(t)}
              y2={y(t)}
              stroke="hsl(var(--border))"
              strokeDasharray="3 3"
            />
            <text
              x={padL - 6}
              y={y(t)}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize={10}
              fontFamily="monospace"
              fill="hsl(var(--muted-foreground))"
            >
              {t}
            </text>
          </g>
        ))}

        {boxes.map((b, i) => {
          const cx = padL + colWidth * (i + 0.5);
          const x = cx - boxWidth / 2;
          const fill = colorByOutcome[b.outcome] ?? "hsl(var(--primary))";
          return (
            <g key={b.outcome}>
              <title>
                {b.outcome} · n={b.count} · min {b.min_ms} · q1 {b.q1_ms} · med {b.median_ms} · q3 {b.q3_ms} · max {b.max_ms} (ms)
              </title>
              {/* whisker */}
              <line x1={cx} x2={cx} y1={y(b.min_ms)} y2={y(b.max_ms)} stroke={fill} strokeWidth={1.5} />
              <line x1={cx - 8} x2={cx + 8} y1={y(b.min_ms)} y2={y(b.min_ms)} stroke={fill} strokeWidth={1.5} />
              <line x1={cx - 8} x2={cx + 8} y1={y(b.max_ms)} y2={y(b.max_ms)} stroke={fill} strokeWidth={1.5} />
              {/* box */}
              <rect
                x={x}
                y={y(b.q3_ms)}
                width={boxWidth}
                height={Math.max(1, y(b.q1_ms) - y(b.q3_ms))}
                fill={fill}
                fillOpacity={0.25}
                stroke={fill}
                strokeWidth={1.5}
                rx={2}
              />
              {/* median */}
              <line
                x1={x}
                x2={x + boxWidth}
                y1={y(b.median_ms)}
                y2={y(b.median_ms)}
                stroke={fill}
                strokeWidth={2}
              />
              {/* x label */}
              <text
                x={cx}
                y={H - padB + 16}
                textAnchor="middle"
                fontSize={10}
                fontFamily="monospace"
                fill="hsl(var(--muted-foreground))"
              >
                {b.outcome}
              </text>
              <text
                x={cx}
                y={H - padB + 26}
                textAnchor="middle"
                fontSize={9}
                fontFamily="monospace"
                fill="hsl(var(--muted-foreground))"
                opacity={0.7}
              >
                n={b.count}
              </text>
            </g>
          );
        })}

        {/* Axis line */}
        <line x1={padL} x2={W - padR} y1={H - padB} y2={H - padB} stroke="hsl(var(--border))" />
      </svg>
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div className="h-56 flex items-end gap-1.5 px-1 animate-pulse" aria-label="Loading chart">
      {Array.from({ length: 24 }).map((_, i) => (
        <div
          key={i}
          className="flex-1 rounded-sm bg-muted"
          style={{ height: `${20 + ((i * 37) % 70)}%` }}
        />
      ))}
    </div>
  );
}

function ChartEmpty({ message }: { message: string }) {
  return (
    <div className="h-56 flex flex-col items-center justify-center text-center gap-1 border border-dashed border-border rounded-md">
      <div className="font-mono text-[10px] uppercase text-muted-foreground">No data</div>
      <p className="text-sm text-muted-foreground max-w-xs">{message}</p>
    </div>
  );
}

function ChartError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="h-56 flex flex-col items-center justify-center text-center gap-2 border border-dashed border-destructive/40 bg-destructive/5 rounded-md px-4">
      <div className="font-mono text-[10px] uppercase text-destructive">Chart failed to load</div>
      <p className="text-sm text-muted-foreground max-w-xs break-words">{message}</p>
      <Button size="sm" variant="outline" onClick={onRetry}>Retry</Button>
    </div>
  );
}

function ChartRetrying({
  attempt,
  max,
  delayMs,
  startedAt,
  remainingDelays,
  onCancel,
  cancelDisabled = false,
}: {
  attempt: number;
  max: number;
  delayMs: number;
  startedAt: number;
  remainingDelays: number[];
  onCancel: () => void;
  cancelDisabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const handleConfirm = () => {
    setOpen(false);
    onCancel();
  };
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(id);
  }, [startedAt]);
  const remaining = Math.max(0, delayMs - (now - startedAt));
  const remainingSec = (remaining / 1000).toFixed(1);
  const progress = delayMs > 0 ? Math.min(100, ((delayMs - remaining) / delayMs) * 100) : 100;
  const totalRemainingMs = remaining + remainingDelays.reduce((a, b) => a + b, 0);
  const totalRemainingSec = (totalRemainingMs / 1000).toFixed(1);

  const remainingAttempts = max - attempt;

  return (
    <div className="h-56 flex flex-col items-center justify-center text-center gap-3 border border-dashed border-border rounded-md px-6">
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase text-muted-foreground">
        <span className="inline-block h-2 w-2 rounded-full bg-primary animate-pulse" />
        Retrying latency chart…
      </div>

      {/* Step pips for the full retry sequence */}
      <div className="flex items-center gap-1.5" aria-label={`Retry ${attempt} of ${max}`}>
        {Array.from({ length: max }).map((_, i) => {
          const state = i + 1 < attempt ? "done" : i + 1 === attempt ? "active" : "pending";
          return (
            <span
              key={i}
              className={cn(
                "h-2 w-6 rounded-full transition-colors",
                state === "done" && "bg-destructive/60",
                state === "active" && "bg-primary animate-pulse",
                state === "pending" && "bg-muted",
              )}
            />
          );
        })}
      </div>

      <p className="text-sm text-foreground font-medium">
        Retry {attempt} of {max} running · next attempt in {remainingSec}s
      </p>
      <p className="font-mono text-[10px] uppercase text-muted-foreground">
        {remainingAttempts === 0
          ? "Final attempt — error state next if this fails"
          : `${remainingAttempts} ${remainingAttempts === 1 ? "retry" : "retries"} left · up to ${totalRemainingSec}s remaining`}
      </p>
      <div className="w-48 h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full bg-primary transition-[width] duration-100 ease-linear"
          style={{ width: `${progress}%` }}
        />
      </div>
      <p className="text-xs text-muted-foreground max-w-xs">
        The latency aggregation service didn't respond. We'll automatically retry with a longer wait before showing an error.
      </p>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger asChild>
          <Button size="sm" variant="outline" disabled={cancelDisabled}>
            {cancelDisabled ? "Cancelling…" : "Cancel retries"}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel retry sequence?</AlertDialogTitle>
            <AlertDialogDescription>
              This will stop the remaining {Math.max(0, max - attempt)} retr{max - attempt === 1 ? "y" : "ies"} and immediately show the error state for the latency chart. You can manually retry from the error panel afterwards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep retrying</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Cancel retries
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
